import React, { Component } from 'react';
import get from 'lodash.get';
import {
  Item,
  Grid,
  GridColumn,
  Container,
  Breadcrumb,
  Button,
  Header
} from 'semantic-ui-react';
import GridRow from 'semantic-ui-react/dist/commonjs/collections/Grid/GridRow';
import { Link } from 'react-router-dom';
import { withRouterV6 } from '../../../helpers/withRouterV6';
import { Translate, I18n } from 'react-redux-i18n';
import { connect } from 'react-redux';
import {
  Edit3,
  UserCheck,
  FileText,
  ScrollText,
  ShieldCheck,
  ShieldOff,
  UserX,
  History,
  RotateCcw
} from 'lucide-react';
import { EntityHeaderActions } from '../../../components/common/EntityHeaderActions/EntityHeaderActions';
import CopyButton from '../../../components/common/CopyButton';
import SystemOnlyBadge, {
  SystemOnlyGroup
} from '../../../components/common/SystemOnlyBadge';
import {
  RetentionPanel,
  DeidentifyModal,
  DeidentifyAuditModal,
  getAccountRetention,
  refreshAccountRetention
} from '../../dataRetention';

import {
  donationSummaryModalOpen,
  getRecord
} from './store/accountDetailActions';
import { impersonateAdmin } from '../../../modules/session';

import { Record } from '../../../modules/bin/utility';
import { getAvatarImagePath } from '../../../helpers/imageUrlHelper';

import AccountStatus from '../status';
import Spinner from '../../../components/common/spinner';
import DonationSummaryModalContainer from './donationSummaryModal/donationSummaryModal';

import config from '../../../constants';
import { grecaptcha } from '../../../helpers/grecaptcha';
import {
  getTabFromAccountTypes,
  i18nKeys,
  hasActiveOrPastSystemAdminMembership
} from '../constants';
import { formatTimeElapsedOrDate } from '../../../utils/time/formatTimeElapsed';
import { formatTimeRemaining } from '../../../utils/time/formatTimeRemaining';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

class DetailHeader extends Component {
  state = {
    deidentifyOpen: false,
    revertOpen: false,
    auditOpen: false,
    anonymisedAt: null
  };

  // Reuses the retention request the page already prefetches on mount, so knowing whether there is
  // any history to show costs no extra call. Silent on failure: the panel surfaces the error, and a
  // missing menu item is better than a broken header.
  componentDidMount() {
    if (!this.props.isSystemAdmin) {
      return;
    }
    getAccountRetention(Number(this.props.match.params.id))
      .then((retention) =>
        this.setState({ anonymisedAt: retention.anonymisedAt })
      )
      .catch(() => {});
  }

  onDeidentifyOpen = () => this.setState({ deidentifyOpen: true });

  onDeidentifyClose = () => this.setState({ deidentifyOpen: false });

  // The account's own email and name change, so the header must re-read rather than keep showing
  // the values it rendered with.
  onDeidentifyCompleted = () => {
    this.props.reloadAccount(this.props.match.params.id);
    // The menu itself depends on this, so it has to move with the action rather than wait for a
    // page reload, and it must skip the prefetch cache whose entry is now stale by definition.
    refreshAccountRetention(Number(this.props.match.params.id))
      .then((retention) =>
        this.setState({ anonymisedAt: retention.anonymisedAt })
      )
      .catch(() => {});
  };

  getAccountsTabUrl() {
    if (this.props.accountDetail) {
      const account = this.props.accountDetail;
      return `/accounts/${getTabFromAccountTypes(account)}`;
    }
  }

  generalRedirect = () => {
    this.props.history.push(config.DEFAULT_CAMPAIGNS_URL);
  };

  handleImpersonateClick = () => {
    const tokenCallback = (token) =>
      this.props.impersonateAdmin(this.props.accountDetail.id, token);
    grecaptcha(tokenCallback, 'SessionImpersonate');
  };

  donationSummaryModalOpen = () => {
    this.props.donationSummaryModalOpen(true);
  };

  /**
   * The system-administrator-only fields, built as a list and laid out two per row inside one
   * system-only group.
   *
   * They used to be hand-placed into GridRows, and twice now a new field has been added as a third
   * width-8 column to a row two columns already filled. Semantic wraps it without a vertical
   * gutter, so it lands flush against the field above with no gap. Pairing them here means adding
   * a field cannot reintroduce that.
   */
  renderSystemAdminFieldRows(accountDetail) {
    if (!this.props.isSystemAdmin) {
      return null;
    }

    const relative = (value) =>
      formatTimeElapsedOrDate(value, undefined, {
        relativeTimeOptions: { isUtc: true }
      });
    // Same value, read inside a sentence rather than as a field of its own.
    const relativeInSentence = (value) =>
      formatTimeElapsedOrDate(value, undefined, {
        relativeTimeOptions: { isUtc: true, inSentence: true }
      });
    const emphasis = { color: 'var(--color-error, #d32f2f)', fontWeight: 600 };
    const lockRemaining = formatTimeRemaining(accountDetail.loginLockedUntil);

    const fields = [
      {
        key: 'mfa',
        label: i18nKeys.HAS_MFA_ACTIVE,
        value: (
          <span
            className="label"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            {accountDetail.hasMfaActive ? (
              <>
                <ShieldCheck
                  size={16}
                  color="var(--color-success, #2e7d32)"
                  aria-hidden="true"
                />{' '}
                Active
              </>
            ) : (
              <>
                <ShieldOff
                  size={16}
                  color="var(--color-error, #d32f2f)"
                  aria-hidden="true"
                />{' '}
                Inactive
              </>
            )}
          </span>
        )
      },
      {
        key: 'lastLogin',
        label: i18nKeys.LAST_LOGGED_IN_AT,
        value: (
          <span className="label">
            {accountDetail.lastLoggedInAt ? (
              relative(accountDetail.lastLoggedInAt)
            ) : (
              <Translate value={i18nKeys.NEVER_LOGGED_IN} />
            )}
          </span>
        )
      },
      {
        // Which scheme hashed the password, so a login problem can be diagnosed without a
        // database query. Absent until the account sets one.
        key: 'passwordVersion',
        label: i18nKeys.PASSWORD_VERSION,
        value: (
          <span className="label">
            {accountDetail.passwordVersion ? (
              accountDetail.passwordVersion.toUpperCase()
            ) : (
              <Translate value={i18nKeys.PASSWORD_VERSION_NOT_SET} />
            )}
          </span>
        )
      },
      {
        // Cleared by a password change that passed validation, so a value here means the account
        // was told to rotate and has not, and stays confined to the reset calls until it does.
        key: 'passwordResetRequired',
        label: i18nKeys.PASSWORD_RESET_REQUIRED_AT,
        value: accountDetail.passwordResetRequiredAt ? (
          <span className="label" style={emphasis}>
            {I18n.t(i18nKeys.PASSWORD_RESET_REQUIRED_SINCE, {
              elapsed: relativeInSentence(accountDetail.passwordResetRequiredAt)
            })}
          </span>
        ) : (
          <span className="label">
            <Translate value={i18nKeys.PASSWORD_RESET_NOT_REQUIRED} />
          </span>
        )
      },
      {
        key: 'lastPasswordChanged',
        label: i18nKeys.LAST_PASSWORD_CHANGED_AT,
        value: (
          <span className="label">
            {accountDetail.lastPasswordChangedAt ? (
              relative(accountDetail.lastPasswordChangedAt)
            ) : (
              <Translate value={i18nKeys.PASSWORD_NEVER_CHANGED} />
            )}
          </span>
        )
      },
      lockRemaining && {
        key: 'loginLocked',
        label: i18nKeys.LOGIN_LOCKED_UNTIL,
        value: (
          <span className="label" style={emphasis}>
            {I18n.t(i18nKeys.LOCKED_FOR, { duration: lockRemaining })}
          </span>
        )
      },
      accountDetail.reportedAsSpamAt && {
        key: 'reportedAsSpam',
        label: i18nKeys.REPORTED_AS_SPAM_AT,
        value: (
          <span className="label" style={emphasis}>
            {relative(accountDetail.reportedAsSpamAt)}
          </span>
        )
      }
    ].filter(Boolean);

    // Two per row. A GridRow holding more than 16 columns' worth wraps with no vertical gutter.
    // Built in the one pass that emits them, rather than chunking first and rendering after.
    const rows = [];
    for (let i = 0; i < fields.length; i += 2) {
      const row = fields.slice(i, i + 2);
      rows.push(
        <GridRow key={row.map((field) => field.key).join('-')}>
          {row.map((field) => (
            <GridColumn width={8} key={field.key}>
              <Header as="h4">
                <Translate value={field.label} />
              </Header>
              {field.value}
            </GridColumn>
          ))}
        </GridRow>
      );
    }

    return (
      <GridRow>
        <GridColumn width={16}>
          <SystemOnlyGroup>
            <Grid>{rows}</Grid>
          </SystemOnlyGroup>
        </GridColumn>
      </GridRow>
    );
  }

  render() {
    if (Record.isRecordLoading(this.props.impersonatedRecord)) {
      return <Spinner />;
    }

    if (this.props.needGeneralRedirect) {
      this.generalRedirect();
    }

    const accountDetail = this.props.accountDetail;
    const accountName = `${accountDetail.firstName} ${accountDetail.lastName}`;
    const accountSettingsUrl = `/accounts/settings/${this.props.match.params.id}`;
    const isSystemAdmin = this.props.isSystemAdmin;
    const canImpersonate =
      !this.props.impersonated &&
      !hasActiveOrPastSystemAdminMembership(accountDetail) &&
      isSystemAdmin;
    const isSourceVisible = isSystemAdmin;

    const email = get(accountDetail, 'email');

    // Edit action - shown as primary button
    const editAction = {
      key: 'edit',
      label: I18n.t('account.account-detail.editAccount'),
      icon: <Edit3 aria-hidden="true" />,
      linkTo: `/accounts/settings/${this.props.match.params.id}`
    };

    // Build other action menu items
    const actionMenuItems = [];

    if (canImpersonate) {
      actionMenuItems.push({
        key: CONTEXT_MENU_KEYS.ACCOUNT.IMPERSONATE.key,
        label: I18n.t('account.account-detail.impersonate'),
        icon: <UserCheck aria-hidden="true" />,
        onSelect: this.handleImpersonateClick,
        systemOnly: true
      });
    }

    // Gated on the account holding a system membership, current or revoked: this is the history
    // the credential policy work needs to be able to produce, and a demoted administrator's
    // actions are exactly the ones worth being able to look up. The audit trail filters by actor,
    // so this answers "what did this account do".
    if (isSystemAdmin && hasActiveOrPastSystemAdminMembership(accountDetail)) {
      actionMenuItems.push({
        key: CONTEXT_MENU_KEYS.ACCOUNT.AUDIT_TRAIL.key,
        label: I18n.t('account.account-detail.auditTrail'),
        icon: <ScrollText aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.ACCOUNT.AUDIT_TRAIL.getUrl({
          id: accountDetail.id
        }),
        systemOnly: true
      });
    }

    if (isSystemAdmin) {
      actionMenuItems.push({
        key: 'donation-summary',
        label: I18n.t('account.account-detail.donationSummary'),
        icon: <FileText aria-hidden="true" />,
        onSelect: this.donationSummaryModalOpen,
        systemOnly: true
      });
    }

    // Only where something was actually de-identified; otherwise the item opens an empty modal.
    if (isSystemAdmin && this.state.anonymisedAt) {
      actionMenuItems.push({
        key: 'deidentify-audit',
        label: I18n.t(i18nKeys.RETENTION_AUDIT_ACTION),
        icon: <History aria-hidden="true" />,
        onSelect: () => this.setState({ auditOpen: true }),
        systemOnly: true
      });
    }

    // The three retention actions are mutually exclusive by state: de-identify before, restore and
    // history after. Offering de-identify on an already-scrambled subject would only earn a refusal.
    if (isSystemAdmin && !this.state.anonymisedAt) {
      actionMenuItems.push({
        key: 'deidentify',
        label: I18n.t(i18nKeys.RETENTION_DEIDENTIFY_ACTION),
        icon: <UserX aria-hidden="true" />,
        onSelect: this.onDeidentifyOpen,
        systemOnly: true
      });
    }

    if (isSystemAdmin && this.state.anonymisedAt) {
      actionMenuItems.push({
        key: 'deidentify-revert',
        label: I18n.t(i18nKeys.RETENTION_REVERT_ACTION),
        icon: <RotateCcw aria-hidden="true" />,
        onSelect: () => this.setState({ revertOpen: true }),
        systemOnly: true
      });
    }

    return (
      <Grid>
        <DonationSummaryModalContainer email={email} />
        {this.state.auditOpen && (
          <DeidentifyAuditModal
            accountId={Number(this.props.match.params.id)}
            onClose={() => this.setState({ auditOpen: false })}
          />
        )}
        {this.state.revertOpen && (
          <DeidentifyModal
            mode="revert"
            subjects={[
              {
                accountId: Number(this.props.match.params.id),
                email: accountDetail.email
              }
            ]}
            onClose={() => this.setState({ revertOpen: false })}
            onCompleted={this.onDeidentifyCompleted}
          />
        )}
        {this.state.deidentifyOpen && (
          <DeidentifyModal
            mode="deidentify"
            subjects={[
              {
                accountId: Number(this.props.match.params.id),
                email: accountDetail.email
              }
            ]}
            onClose={this.onDeidentifyClose}
            onCompleted={this.onDeidentifyCompleted}
          />
        )}
        <GridRow>
          <GridColumn width={8}>
            <Item.Group>
              <Item>
                <Item.Image
                  size="tiny"
                  src={getAvatarImagePath(accountDetail.mainImagePath)}
                  avatar
                  className="account-photo"
                />
                <Item.Content>
                  <Item.Header as="a">
                    <h1>{accountName}</h1>
                  </Item.Header>
                  <div>
                    <AccountStatus value={accountDetail.status} />
                  </div>
                </Item.Content>
              </Item>
            </Item.Group>
          </GridColumn>
          <GridColumn width={8}>
            <Container
              textAlign="right"
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <EntityHeaderActions
                editAction={editAction}
                menuItems={actionMenuItems}
                actionsLabel={I18n.t('account.account-detail.actions')}
              />
            </Container>
          </GridColumn>
        </GridRow>
        <GridRow className="margin-top-30 label-container">
          <GridColumn width={isSystemAdmin ? 10 : 16}>
            <Grid>
              <GridRow>
                <GridColumn width={8}>
                  <Header as="h4">
                    <Translate value="account.account-detail.email" />
                  </Header>
                  <span
                    className="label"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {accountDetail.email}
                    <CopyButton value={accountDetail.email} />
                  </span>
                </GridColumn>
                {isSourceVisible && (
                  <GridColumn width={8}>
                    <Header as="h4">
                      <Translate value="account.account-detail.source" />
                      <SystemOnlyBadge />
                    </Header>
                    <span className="label capitalize">
                      {accountDetail.source}
                    </span>
                  </GridColumn>
                )}
              </GridRow>
              {this.renderSystemAdminFieldRows(accountDetail)}
            </Grid>
          </GridColumn>
          {isSystemAdmin && (
            <GridColumn width={6}>
              <SystemOnlyGroup>
                <RetentionPanel accountId={Number(this.props.match.params.id)} />
              </SystemOnlyGroup>
            </GridColumn>
          )}
        </GridRow>
        {/*
        <GridRow className="label-container">
          <GridColumn width={6}>
            <Header as="h4">
              <Translate value="account.account-detail.totalAmountFundraised" />
            </Header>
            <span className="label">{totalAmountFundraised}</span>
          </GridColumn>
          <GridColumn width={6}>
            <Header as="h4">
              <Translate value="account.account-detail.totalAmountDonated" />
            </Header>
            <span className="label">{totalAmountDonated}</span>
          </GridColumn>
        </GridRow>
        */}
      </Grid>
    );
  }
}

const mapState = ({ accountDetail, session, account }) => {
  return {
    accountDetail: accountDetail.detailRecord.data.data,
    impersonated: session.impersonated,
    accountId: get(session, 'account.id'),
    impersonatedRecord: account.impersonatedRecord,
    isOrganizationAdmin: get(session, 'isOrganizationAdmin'),
    isSystemAdmin: get(session, 'isSystemAdmin'),
    needGeneralRedirect: get(session, 'needGeneralRedirect')
  };
};

const mapDispatch = (dispatch) => {
  return {
    impersonateAdmin(id, verificationToken = null) {
      dispatch(impersonateAdmin(id, verificationToken));
    },
    donationSummaryModalOpen(open) {
      dispatch(donationSummaryModalOpen(open));
    },
    reloadAccount(id) {
      dispatch(getRecord(id));
    }
  };
};

const AccountDetailHeaderContainer = withRouterV6(
  connect(mapState, mapDispatch)(DetailHeader)
);
export default AccountDetailHeaderContainer;
